import React, { useContext, useEffect, useState, useRef, useMemo } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import {
  Card,
  Icon,
  Divider,
  Button,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  CircularProgress,
  AppBar,
  Tabs,
  Tab,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { UserContext, LanguageContext } from "App";
import UsersApi from "Classes/API/UsersApi";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Loader from "layouts/loader/loader";
import EntryFile from "./EntryFile";
import PatientSheetDialog from "./PatientSheetDialog";
import CreateEntry from "./CreateEntry";
import Swal from "sweetalert2";
import { getStorage, ref, deleteObject } from "firebase/storage";
import { format, isToday, isYesterday } from "date-fns";
import SessionsApi from "../../Classes/API/Administration/SessionsApi";
import DiagnosisEntry from "./DiagnosisEntry";
import toast, { Toaster } from "react-hot-toast";
import { daysOfWeek } from "layouts/Services/constants";

const PatientEntries = () => {
  const { id } = useParams();
  const { state } = useLocation();
  const navigate = useNavigate();
  const { translations } = useContext(LanguageContext);
  const contextValue = useContext(UserContext);
  const storage = getStorage();

  const diagnosisRef = useRef(null);
  const programRef = useRef(null);
  const entriesRef = useRef(null);
  const entryRef = useRef();

  const [activeTab, setActiveTab] = useState(0);
  const [entries, setEntries] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openPatientSheet, setOpenPatientSheet] = useState(false);
  const [openEntryPopup, setOpenEntryPopup] = useState(false);
  const [isSubmittingEntry, setIsSubmittingEntry] = useState(false);
  const [initialFormStep, setInitialFormStep] = useState(0);
  const [physicalDiagnoses, setPhysicalDiagnoses] = useState([]);

  const isAdmin =
    ["Admin", "Owner", "HeadAccounting"].includes(contextValue?.userType) ||
    contextValue?.userType === "SuperAdmin";
  const IsAdminOrTherapist = isAdmin || contextValue?.userType === "Therapist";

  const fetchData = async () => {
    setLoading(true);
    try {
      const paging = { pageNumber: 1, pageSize: 100 };
      const req = { patientId: id, pagingParams: paging };
      const [entriesRes, programsRes, sheetsRes] = await Promise.all([
        UsersApi.SearchPatientHistoryEntries(req),
        SessionsApi.GetPatientSheet(id),
        SessionsApi.GetPatientPhysicalDiagnoses(id),
      ]);
      setEntries(entriesRes.data?.list || []);
      setPrograms(programsRes.data || []);
      setPhysicalDiagnoses(sheetsRes.data || []);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [id]);

  useEffect(() => {
    const sections = [
      { ref: diagnosisRef, index: 0 },
      { ref: programRef, index: 1 },
      { ref: entriesRef, index: 2 },
    ];

    const handleScroll = () => {
      const offset = 180; // sync with scrollToSection offset
      const scrollY = window.scrollY + offset;
      let currentTab = 0;

      sections.forEach(({ ref, index }) => {
        if (!ref.current) return;
        const top = ref.current.offsetTop;
        if (scrollY >= top) {
          currentTab = index;
        }
      });

      setActiveTab(currentTab);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();

    return () => window.removeEventListener("scroll", handleScroll);
  }, [loading]);

  const scrollToSection = (ref, index) => {
    setActiveTab(index);
    const offset = 160;
    const bodyRect = document.body.getBoundingClientRect().top;
    const elementRect = ref.current.getBoundingClientRect().top;
    const elementPosition = elementRect - bodyRect;
    const offsetPosition = elementPosition - offset;
    window.scrollTo({ top: offsetPosition, behavior: "smooth" });
  };

  const handleOpenForm = (stageIndex) => {
    setInitialFormStep(stageIndex);
    setOpenPatientSheet(true);
  };

  const handleSaveEntry = async () => {
    if (entryRef.current) {
      setIsSubmittingEntry(true);
      try {
        const success = await entryRef.current.submit();
        if (success) {
          setOpenEntryPopup(false);
          fetchData();
        }
      } catch (error) {
        toast.error("Error saving entry");
      } finally {
        setIsSubmittingEntry(false);
      }
    }
  };

  const handleDeleteEntry = async (entry) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      confirmButtonText: translations.Delete,
    });
    if (!result.isConfirmed) return;
    if (entry.fileUrl && entry.filePath) {
      try {
        await deleteObject(ref(storage, entry.filePath));
      } catch (e) {
        console.error(e);
      }
    }
    const res = await UsersApi.DeletePatientHistoryEntry({
      SearchInt: entry.id,
    });
    if (res.status === "Success") fetchData();
  };

  const handleDeleteDiagnosis = async (diagId) => {
    const result = await Swal.fire({
      title: "Delete Diagnosis?",
      text: "This mapping will be permanently removed.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      confirmButtonText: "Yes, delete it",
    });
    if (result.isConfirmed) {
      const res = await SessionsApi.DeletePatientPhysicalDiagnosis(diagId);
      if (res.status === "Success") {
        toast.success("Diagnosis deleted successfully");
        fetchData();
      }
    }
  };

  const handleDeleteProgram = async (programId) => {
    const result = await Swal.fire({
      title: "Delete Program?",
      text: "This session will be removed from the patient's plan.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      confirmButtonText: "Yes, delete it",
    });
    if (result.isConfirmed) {
      const res = await SessionsApi.DeletePatientSheet(programId);
      if (res.status === "Success") {
        toast.success("Program session deleted");
        fetchData();
      }
    }
  };

  const formatEntryDate = (timestamp) => {
    const date = new Date(timestamp);
    if (isToday(date))
      return { primary: "Today", secondary: format(date, "hh:mm a") };
    if (isYesterday(date))
      return { primary: "Yesterday", secondary: format(date, "hh:mm a") };
    return {
      primary: format(date, "EEE, MMM d"),
      secondary: `${format(date, "yyyy")} • ${format(date, "hh:mm a")}`,
    };
  };

  const getSelectedDayLabels = (selectedDaysString) => {
    if (!selectedDaysString || selectedDaysString.trim() === "") return [];
    const dayNumbers = selectedDaysString
      .split(",")
      .map((d) => parseInt(d, 10));
    return daysOfWeek
      .filter((day) => dayNumbers.includes(day.value))
      .map((day) => day.label);
  };

  const groupedPrograms = useMemo(() => {
    return programs.reduce((groups, prog) => {
      const date = new Date(prog.createdAt).toLocaleDateString("en-GB");
      if (!groups[date]) groups[date] = [];
      groups[date].push(prog);
      return groups;
    }, {});
  }, [programs]);

  const SectionWrapper = ({
    children,
    title,
    description,
    onAdd,
    showAdd,
    sectionRef,
    color = "info",
  }) => (
    <MDBox ref={sectionRef} mb={5} sx={{ scrollMarginTop: "160px" }}>
      <Card
        sx={{
          borderRadius: "20px",
          border: "1px solid #f0f2f5",
          boxShadow: "0 10px 30px rgba(0,0,0,0.03)",
        }}
      >
        <MDBox p={4}>
          <MDBox
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
          >
            <MDBox>
              <MDTypography variant="h4" fontWeight="bold">
                {title}
              </MDTypography>
              <MDTypography variant="button" color="text">
                {description}
              </MDTypography>
            </MDBox>
            {showAdd && (
              <MDButton
                variant="gradient"
                color={color}
                onClick={onAdd}
                sx={{ borderRadius: "10px" }}
              >
                <Icon sx={{ fontWeight: "bold", mr: 1 }}>add</Icon> Add
              </MDButton>
            )}
          </MDBox>
          <Divider sx={{ my: 3 }} />
          {children}
        </MDBox>
      </Card>
    </MDBox>
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Toaster position="top-right" />
      {loading ? (
        <Loader />
      ) : (
        <>
          <AppBar
            position="sticky"
            sx={{
              top: "100px",
              zIndex: 1100,
              backgroundColor: "rgb(255, 255, 255)",
              borderRadius: "24px",
              width: "90%",
              margin: "0 auto 40px",
              padding: "6px",
              boxShadow: "0 10px 40px rgba(0,0,0,0.1)",
              display: { xs: "none", md: "block" },
            }}
          >
            <Tabs
              value={activeTab}
              variant="scrollable"
              scrollButtons={false}
              sx={{
                minHeight: "48px",
                "& .MuiTabs-indicator": {
                  height: "100%",
                  borderRadius: "18px",
                  backgroundColor: "info.main",
                  zIndex: 0,
                  opacity: 0.15,
                },
                "& .MuiTabs-flexContainer": {
                  gap: "8px",
                },
              }}
            >
              {[
                { label: "Physical Diagnosis", ref: diagnosisRef, index: 0 },
                { label: "Patient Program", ref: programRef, index: 1 },
                { label: "Clinical Entries", ref: entriesRef, index: 2 },
              ].map((tab) => (
                <Tab
                  key={tab.index}
                  label={tab.label}
                  onClick={() => scrollToSection(tab.ref, tab.index)}
                  sx={{
                    zIndex: 1,
                    minHeight: "48px",
                    textTransform: "none",
                    fontWeight: activeTab === tab.index ? 700 : 500,
                    fontSize: "0.875rem",
                    color: "#344767",
                    borderRadius: "18px",
                    transition: "all 0.2s ease",
                    "&.Mui-selected": {
                      color: "info.main",
                    },
                    "&:hover": {
                      backgroundColor: "rgba(0, 0, 0, 0.04)",
                      color: "info.main",
                    },
                  }}
                />
              ))}
            </Tabs>
          </AppBar>

          <MDBox py={3} px={3} sx={{ maxWidth: "1400px", margin: "0 auto" }}>
            <MDBox
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={6}
            >
              <MDButton
                variant="outlined"
                color="info"
                onClick={() => navigate(-1)}
              >
                {translations.Back}
              </MDButton>
              <MDBox textAlign="center">
                <MDTypography variant="h2" fontWeight="bold">
                  {state?.patient?.name}
                </MDTypography>
                <MDTypography variant="button" color="info" fontWeight="bold">
                  Patient ID: #{id}
                </MDTypography>
              </MDBox>
              <MDBox width="100px" />
            </MDBox>

            {/* Section 1: Physical Diagnosis History */}
            <SectionWrapper
              sectionRef={diagnosisRef}
              title="Physical Diagnosis History"
              description="Visual body mapping"
              onAdd={() => handleOpenForm(0)}
              showAdd={isAdmin}
            >
              {physicalDiagnoses.length === 0 ? (
                <MDTypography variant="body2" textAlign="center" py={3}>
                  No records.
                </MDTypography>
              ) : (
                physicalDiagnoses.map((diag) => (
                  <DiagnosisEntry
                    key={diag.id}
                    entry={diag}
                    isAdmin={isAdmin}
                    onDelete={handleDeleteDiagnosis}
                  />
                ))
              )}
            </SectionWrapper>

            {/* Section 2: Patient Program */}
            <SectionWrapper
              sectionRef={programRef}
              title="Patient Program"
              description="Exercise plans"
              onAdd={() => handleOpenForm(1)}
              showAdd={isAdmin}
            >
              {programs.length === 0 ? (
                <MDTypography variant="body2" textAlign="center" py={3}>
                  No programs.
                </MDTypography>
              ) : (
                Object.keys(groupedPrograms).map((date) => {
                  const sessionsInDay = groupedPrograms[date];
                  return (
                    <MDBox
                      key={date}
                      mb={3}
                      sx={{
                        border: "1px solid #f0f2f5",
                        borderRadius: "12px",
                        overflow: "hidden",
                        backgroundColor: "#fff",
                      }}
                    >
                      <MDBox
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        p={1.5}
                        sx={{
                          backgroundColor: "#f8f9fa",
                          borderBottom: "1px solid #f0f2f5",
                        }}
                      >
                        <MDBox display="flex" alignItems="center" gap={1}>
                          <Icon
                            color="info"
                            sx={{ fontSize: "1.1rem !important" }}
                          >
                            calendar_today
                          </Icon>
                          <MDTypography
                            variant="button"
                            fontWeight="bold"
                            color="dark"
                          >
                            {date}
                          </MDTypography>
                          <MDBox
                            px={1}
                            sx={{ bgcolor: "#e3f2fd", borderRadius: "4px" }}
                          >
                            <MDTypography
                              variant="caption"
                              color="info"
                              fontWeight="bold"
                            >
                              {sessionsInDay.length} Sessions
                            </MDTypography>
                          </MDBox>
                        </MDBox>
                        {sessionsInDay?.[0]?.discountPercent > 0 && (
                          <MDBox
                            px={1.5}
                            py={0.5}
                            sx={{ bgcolor: "#4CAF50", borderRadius: "6px" }}
                          >
                            <MDTypography
                              variant="caption"
                              color="white"
                              fontWeight="bold"
                            >
                              OFF:{" "}
                              {Number(sessionsInDay[0].discountPercent).toFixed(
                                0,
                              )}
                              %
                            </MDTypography>
                          </MDBox>
                        )}
                      </MDBox>
                      {sessionsInDay.map((prog, index) => (
                        <MDBox
                          key={prog.id || index}
                          p={2}
                          sx={{
                            borderBottom:
                              index !== sessionsInDay.length - 1
                                ? "1px solid #f0f2f5"
                                : "none",
                          }}
                        >
                          <Grid container alignItems="center" spacing={2}>
                            <Grid item xs={10} md={5}>
                              <MDTypography
                                variant="h6"
                                sx={{ fontSize: "0.9rem" }}
                              >
                                {prog.sessionName}
                              </MDTypography>
                              <MDTypography variant="caption" color="text">
                                By: {prog.createdByName}
                              </MDTypography>
                            </Grid>
                            <Grid item xs={4} md={3}>
                              <MDTypography variant="caption" fontWeight="bold">
                                QTY:{" "}
                                <span style={{ fontWeight: "normal" }}>
                                  {prog.quantity}
                                </span>
                              </MDTypography>
                            </Grid>
                            <Grid item xs={4} md={3}>
                              <MDTypography variant="caption" fontWeight="bold">
                                FREQ:{" "}
                                <span style={{ fontWeight: "normal" }}>
                                  {prog.frequency}
                                </span>
                              </MDTypography>
                              {prog.frequency === "Custom" &&
                                prog.selectedDays && (
                                  <MDBox mt={0.5}>
                                    <MDTypography
                                      variant="caption"
                                      color="info"
                                      fontWeight="bold"
                                    >
                                      Days:{" "}
                                      <span style={{ fontWeight: "normal" }}>
                                        {getSelectedDayLabels(
                                          prog.selectedDays,
                                        ).join(", ")}
                                      </span>
                                    </MDTypography>
                                  </MDBox>
                                )}
                            </Grid>
                            <Grid item xs={2} md={1} textAlign="right">
                              {isAdmin && (
                                <IconButton
                                  color="error"
                                  size="small"
                                  onClick={() => handleDeleteProgram(prog.id)}
                                >
                                  <Icon>delete</Icon>
                                </IconButton>
                              )}
                            </Grid>
                          </Grid>
                        </MDBox>
                      ))}
                    </MDBox>
                  );
                })
              )}
            </SectionWrapper>

            <SectionWrapper
              sectionRef={entriesRef}
              title="Clinical Entries"
              description="Daily session notes"
              onAdd={() => setOpenEntryPopup(true)}
              showAdd={true}
            >
              {entries.length === 0 ? (
                <MDTypography variant="body2" textAlign="center">
                  No entries recorded.
                </MDTypography>
              ) : (
                <MDBox display="flex" flexDirection="column" gap={3}>
                  {entries.map((entry) => {
                    const dateInfo = formatEntryDate(entry.timeStamp);
                    return (
                      <Card
                        key={entry.id}
                        sx={{
                          width: "100%",
                          border: "1px solid #e0e0e0",
                          boxShadow: "none",
                        }}
                      >
                        <MDBox
                          variant="gradient"
                          bgColor="info"
                          p={2}
                          sx={{ borderRadius: "10px 10px 0 0" }}
                        >
                          <MDTypography variant="h6" color="white">
                            {dateInfo.primary}
                          </MDTypography>
                          <MDTypography
                            variant="caption"
                            color="white"
                            opacity={0.8}
                          >
                            {dateInfo.secondary}
                          </MDTypography>
                        </MDBox>
                        <MDBox p={3}>
                          {entry.fileUrl && <EntryFile entry={entry} />}
                          <MDTypography
                            variant="body2"
                            sx={{
                              whiteSpace: "pre-wrap",
                              mt: 2,
                              color: "#4f5e71",
                            }}
                          >
                            {entry.notes}
                          </MDTypography>
                          <Divider sx={{ my: 3 }} />
                          <MDBox
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                          >
                            <MDTypography variant="caption" fontWeight="medium">
                              <Icon
                                sx={{
                                  verticalAlign: "middle",
                                  mr: 0.5,
                                  fontSize: "small",
                                }}
                              >
                                person
                              </Icon>
                              Recorded By: {entry?.getCreatedBy?.name}
                            </MDTypography>
                            {isAdmin && (
                              <MDButton
                                variant="text"
                                color="error"
                                size="small"
                                onClick={() => handleDeleteEntry(entry)}
                              >
                                <Icon sx={{ mr: 0.5 }}>delete</Icon>{" "}
                                {translations.Delete}
                              </MDButton>
                            )}
                          </MDBox>
                        </MDBox>
                      </Card>
                    );
                  })}
                </MDBox>
              )}
            </SectionWrapper>
          </MDBox>
        </>
      )}

      {/* Dialogs */}
      <PatientSheetDialog
        open={openPatientSheet}
        initialStep={initialFormStep}
        onClose={() => {
          setOpenPatientSheet(false);
          fetchData();
        }}
        patient={state?.patient}
      />
      <Dialog
        open={openEntryPopup}
        onClose={isSubmittingEntry ? null : () => setOpenEntryPopup(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          <MDBox
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            Create New Entry{" "}
            <IconButton
              onClick={() => setOpenEntryPopup(false)}
              disabled={isSubmittingEntry}
            >
              <CloseIcon />
            </IconButton>
          </MDBox>
        </DialogTitle>
        <DialogContent dividers>
          <CreateEntry ref={entryRef} isDialog={true} />
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <MDButton
            color="secondary"
            onClick={() => setOpenEntryPopup(false)}
            disabled={isSubmittingEntry}
          >
            Cancel
          </MDButton>
          <MDButton
            variant="gradient"
            color="info"
            onClick={handleSaveEntry}
            disabled={isSubmittingEntry}
          >
            {isSubmittingEntry ? <CircularProgress size={20} /> : "Save Entry"}
          </MDButton>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
};

export default PatientEntries;
